import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { 
  Auth, 
  getAuth, 
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider, 
  OAuthProvider,
  signOut,
  onAuthStateChanged,
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInAnonymously
} from 'firebase/auth';
import { initializeApp, FirebaseApp } from 'firebase/app';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { redirectToMarketing } from '../utils/host-url.util';

/** Full user config returned by GET /api/auth/me (the app bootstraps from this). */
export interface UserConfig {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  tier: string;
  paid: boolean;
  /** Soft-delete flag: the account is deactivated but its data is retained and can be restored. */
  deleted?: boolean;
  deletedAt?: string | null;
  settings?: Record<string, any>;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private app: FirebaseApp;
  private auth: Auth;
  private currentUserSubject: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  public currentUser$: Observable<User | null> = this.currentUserSubject.asObservable();
  /** Full config from the Spring API (membership, profile, settings). */
  private userConfigSubject: BehaviorSubject<UserConfig | null> = new BehaviorSubject<UserConfig | null>(null);
  public userConfig$: Observable<UserConfig | null> = this.userConfigSubject.asObservable();
  /** Headers forwarded to the API in local mode (loaded from assets/local-auth.json). */
  private localHeaders: Record<string, string> | null = null;
  private authInitialized: boolean = false;
  private authInitPromise: Promise<void>;

  constructor(private router: Router, private http: HttpClient) {
    // Initialize Firebase
    this.app = initializeApp(environment.firebase);
    this.auth = getAuth(this.app);

    // ── Local mode: skip Firebase sign-in ─────────────────────────────
    // Identity comes from a local headers file (assets/local-auth.json) that Angular
    // forwards to the Spring API; the API (auth mode=dev) trusts it and returns the
    // full user config from the DB, which the app bootstraps from.
    if (this.isLocalBypass()) {
      this.authInitPromise = this.bootstrapLocalUser();
      return;
    }

    // Check localStorage first to see if there was a previous session
    this.checkLocalStorageSession();

    // Wait for Firebase Auth to restore session and listen to auth state changes
    this.authInitPromise = (async () => {
      // Check for redirect result first (for mobile OAuth)
      // This must happen before onAuthStateChanged to catch the redirect
      await this.checkRedirectResult();
      
      // Wait for Firebase Auth to restore session and listen to auth state changes
      return new Promise<void>((resolve) => {
        onAuthStateChanged(this.auth, (user) => {
          // Mark as initialized after first auth state is determined
          if (!this.authInitialized) {
            this.authInitialized = true;
            resolve();
          }
          
          this.currentUserSubject.next(user);
          if (user) {
            console.log('User signed in:', user.email);
            // Store auth session in localStorage
            try {
              localStorage.setItem('auth_user', JSON.stringify({
                uid: user.uid,
                email: user.email,
                displayName: user.displayName,
                photoURL: user.photoURL
              }));
            } catch (e) {
              console.error('Error storing auth session:', e);
            }
            // Forward the Firebase identity to the API and load the full user config.
            void this.refreshUserConfig();
          } else {
            this.userConfigSubject.next(null);
            console.log('User signed out');
            // Clear auth session from localStorage
            try {
              localStorage.removeItem('auth_user');
            } catch (e) {
              console.error('Error clearing auth session:', e);
            }
          }
        });
      });
    })();
  }

  /**
   * Single source of truth for auth bypass. Requires BOTH the flag AND env === 'local',
   * so even a misconfigured `localAuthBypass: true` on dev/stage/prod can never skip
   * Firebase auth — bypass is structurally impossible outside local.
   */
  isLocalBypass(): boolean {
    return environment.localAuthBypass === true && environment.name === 'local';
  }

  /**
   * Local-mode bootstrap: load the local headers file, forward it to the API, and
   * build the current user from the returned config (data lives in the DB, not a stub).
   */
  private async bootstrapLocalUser(): Promise<void> {
    await this.loadLocalHeaders();
    try {
      const config = await firstValueFrom(
        this.http.get<UserConfig>(`${environment.apiBaseUrl}/api/auth/me`)
      );
      this.userConfigSubject.next(config);
      this.currentUserSubject.next(this.userFromConfig(config));
    } catch (e) {
      // No magic fallback user — local mode just forwards the header identity and lets the
      // API auto-provision it. A failure here means the API is down or the header is missing.
      console.error('Local auth bootstrap failed (is the API running?). No session established:', e);
      this.currentUserSubject.next(null);
      this.userConfigSubject.next(null);
    }
    this.authInitialized = true;
  }

  /**
   * Load the selected local user file: assets/local-auth/<localAuthUser>.json.
   * Switch users by changing environment.localAuthUser (user1 | user2).
   */
  private async loadLocalHeaders(): Promise<void> {
    if (this.localHeaders) return;
    const which = (environment as any).localAuthUser || 'user1';
    const path = `assets/local-auth/${which}.json`;
    try {
      const file = await firstValueFrom(
        this.http.get<{ headers?: Record<string, string> } & Record<string, string>>(path)
      );
      this.localHeaders = (file && file.headers) ? file.headers : (file as Record<string, string>);
    } catch {
      console.warn(`${path} not found — using default dev header (Bearer user1)`);
      this.localHeaders = { Authorization: 'Bearer user1' };
    }
  }

  /** Best-effort uid from the local Authorization header (dev mode: token == uid). */
  private uidFromHeaders(): string | null {
    const auth = this.localHeaders?.['Authorization'] || this.localHeaders?.['authorization'];
    if (auth?.startsWith('Bearer ')) return auth.substring(7).trim();
    return null;
  }

  /** Build a User-shaped object the rest of the app reads, sourced from the API config. */
  private userFromConfig(config: UserConfig): User {
    const stub = {
      uid: config.uid,
      email: config.email,
      displayName: config.displayName,
      photoURL: config.photoURL,
      emailVerified: true,
      isAnonymous: false,
      providerId: 'local',
      providerData: [],
      metadata: {},
      getIdToken: async () => (this.localHeaders ? (this.uidFromHeaders() || '') : ''),
      getIdTokenResult: async () => ({ token: '', claims: {} }),
      reload: async () => undefined,
      delete: async () => undefined,
      toJSON: () => ({ uid: config.uid, email: config.email }),
    };
    return stub as unknown as User;
  }

  /** Latest full user config from the API (membership, profile, settings). */
  getUserConfig(): UserConfig | null {
    return this.userConfigSubject.value;
  }

  /** Re-fetch the user config from the API (e.g. after a real Firebase sign-in). */
  async refreshUserConfig(): Promise<UserConfig | null> {
    try {
      const config = await firstValueFrom(
        this.http.get<UserConfig>(`${environment.apiBaseUrl}/api/auth/me`)
      );
      this.userConfigSubject.next(config);
      return config;
    } catch {
      return null;
    }
  }

  /** Return the cached config, or fetch it once if not yet loaded (used by route guards). */
  async ensureConfigLoaded(): Promise<UserConfig | null> {
    return this.userConfigSubject.value ?? await this.refreshUserConfig();
  }

  /** True if the API has soft-deleted this account (awaiting restore or permanent removal). */
  isAccountDeleted(): boolean {
    return !!this.userConfigSubject.value?.deleted;
  }

  /**
   * Soft-delete the current account via the API. The record and all associated data are
   * RETAINED and can be restored — we do not delete the Firebase user, so the owner can sign
   * back in to restore. (Replaces the old Firebase client-side hard delete.)
   */
  async deleteAccount(): Promise<void> {
    const uid = this.currentUserSubject.value?.uid ?? this.userConfigSubject.value?.uid;
    if (!uid) throw new Error('No authenticated user to delete');
    await firstValueFrom(this.http.delete<void>(`${environment.apiBaseUrl}/api/users/${uid}`));
    await this.refreshUserConfig(); // config.deleted is now true
  }

  /** Restore a previously soft-deleted account, then refresh the config. */
  async restoreAccount(): Promise<UserConfig | null> {
    const uid = this.currentUserSubject.value?.uid ?? this.userConfigSubject.value?.uid;
    if (!uid) throw new Error('No authenticated user to restore');
    await firstValueFrom(this.http.post(`${environment.apiBaseUrl}/api/users/${uid}/restore`, {}));
    return this.refreshUserConfig();
  }

  /**
   * Headers to forward to the Spring API. Local mode: the local headers file.
   * Live mode: the real Firebase ID token as a Bearer header.
   */
  async getApiHeaders(): Promise<Record<string, string>> {
    if (this.isLocalBypass()) {
      await this.loadLocalHeaders();
      return this.localHeaders ?? {};
    }
    const user = this.currentUserSubject.value;
    if (!user) return {};
    try {
      const token = await user.getIdToken();
      return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
      return {};
    }
  }

  /**
   * Check localStorage for previous session
   */
  private checkLocalStorageSession(): void {
    try {
      const storedUser = localStorage.getItem('auth_user');
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        console.log('Found stored session for:', userData.email);
        // Firebase Auth will restore the session automatically via onAuthStateChanged
        // We just log that we found a stored session
      }
    } catch (e) {
      console.error('Error checking localStorage session:', e);
    }
  }

  /**
   * Check for OAuth redirect result (for mobile)
   * This handles OAuth redirects from both automaeta.com and askthealgorithm.com
   */
  private async checkRedirectResult(): Promise<void> {
    try {
      const result = await getRedirectResult(this.auth);
      if (result && result.user) {
        console.log('OAuth redirect successful:', result.user.email);
        // Update current user immediately
        this.currentUserSubject.next(result.user);
        // Store auth session in localStorage
        try {
          localStorage.setItem('auth_user', JSON.stringify({
            uid: result.user.uid,
            email: result.user.email,
            displayName: result.user.displayName,
            photoURL: result.user.photoURL
          }));
        } catch (e) {
          console.error('Error storing auth session:', e);
        }
        // Navigate to home page after successful redirect
        // This works on both automaeta.com and askthealgorithm.com
        // Firebase automatically redirects back to the domain the user came from
        const currentPath = window.location.pathname;
        console.log('Current path after redirect:', currentPath);
        if (currentPath.includes('/login') || currentPath === '/') {
          // Use a longer delay to ensure auth state is fully updated
          // Also wait for Angular to be ready
          setTimeout(() => {
            console.log('Navigating to dashboard after redirect');
            this.router.navigate(['/v2/dashboard']).then(() => {
              console.log('Navigation successful');
            }).catch((err) => {
              console.error('Navigation error:', err);
              // Force reload if navigation fails
              window.location.href = '/v2/dashboard';
            });
          }, 500);
        }
      } else {
        console.log('No redirect result found');
      }
    } catch (error: any) {
      // Only log if it's not a "no redirect" error
      if (error.code !== 'auth/no-auth-event') {
        console.error('Error checking redirect result:', error);
      } else {
        console.log('No auth event (normal if not returning from OAuth redirect)');
      }
    }
  }

  /**
   * Check if device is mobile
   */
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  }

  /**
   * Wait for auth initialization to complete
   */
  async waitForAuthInit(): Promise<void> {
    return this.authInitPromise;
  }

  /**
   * Check if auth has been initialized
   */
  isAuthInitialized(): boolean {
    return this.authInitialized;
  }

  /**
   * Sign in with Google
   * Uses redirect for mobile devices, popup for desktop
   */
  async signInWithGoogle(): Promise<void> {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');
    
    try {
      // Always use popup for now (more reliable)
      // Redirect can cause issues with navigation
      const result = await signInWithPopup(this.auth, provider);
      const user = result.user;
      
      console.log('Google sign-in successful:', user.email);
      
      // Update current user immediately
      this.currentUserSubject.next(user);
      
      // Store auth session in localStorage
      try {
        localStorage.setItem('auth_user', JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        }));
      } catch (e) {
        console.error('Error storing auth session:', e);
      }
      
      // Redirect to dashboard after successful login
      this.router.navigate(['/v2/dashboard']);
    } catch (error: any) {
      console.error('Google sign-in error:', error);
      
      // If popup fails due to popup blocker, try redirect as fallback
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        console.log('Popup blocked or closed, trying redirect flow');
        try {
          await signInWithRedirect(this.auth, provider);
          // Navigation will happen after redirect completes via checkRedirectResult
          return;
        } catch (redirectError: any) {
          console.error('Redirect also failed:', redirectError);
          throw this.handleAuthError(redirectError);
        }
      }
      
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign in with Apple
   * Uses redirect for mobile devices, popup for desktop
   */
  async signInWithApple(): Promise<void> {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    
    try {
      // Always use popup for now (more reliable)
      const result = await signInWithPopup(this.auth, provider);
      const user = result.user;
      
      console.log('Apple sign-in successful:', user.email);
      
      // Update current user immediately
      this.currentUserSubject.next(user);
      
      // Store auth session in localStorage
      try {
        localStorage.setItem('auth_user', JSON.stringify({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL
        }));
      } catch (e) {
        console.error('Error storing auth session:', e);
      }
      
      // Redirect to dashboard after successful login
      this.router.navigate(['/v2/dashboard']);
    } catch (error: any) {
      console.error('Apple sign-in error:', error);
      
      // If popup fails due to popup blocker, try redirect as fallback
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/popup-closed-by-user') {
        console.log('Popup blocked or closed, trying redirect flow');
        try {
          await signInWithRedirect(this.auth, provider);
          // Navigation will happen after redirect completes via checkRedirectResult
          return;
        } catch (redirectError: any) {
          console.error('Redirect also failed:', redirectError);
          throw this.handleAuthError(redirectError);
        }
      }
      
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign up with email and password
   */
  async signUp(email: string, password: string): Promise<void> {
    try {
      const userCredential = await createUserWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;
      
      console.log('Sign up successful:', user.email);
      
      // Redirect to home page after successful signup
      this.router.navigate(['/']);
    } catch (error: any) {
      console.error('Sign up error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign in with email and password
   */
  async signIn(email: string, password: string): Promise<void> {
    try {
      const userCredential = await signInWithEmailAndPassword(this.auth, email, password);
      const user = userCredential.user;
      
      console.log('Sign in successful:', user.email);
      
      // Redirect to dashboard after successful login
      this.router.navigate(['/v2/dashboard']);
    } catch (error: any) {
      console.error('Sign in error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign in anonymously
   */
  async signInAnonymously(): Promise<void> {
    try {
      const userCredential = await signInAnonymously(this.auth);
      const user = userCredential.user;
      
      console.log('Anonymous sign in successful:', user.uid);
      
      // Redirect to home page after successful anonymous login
      this.router.navigate(['/']);
    } catch (error: any) {
      console.error('Anonymous sign in error:', error);
      throw this.handleAuthError(error);
    }
  }

  /**
   * Sign out the current user
   */
  async signOut(): Promise<void> {
    try {
      // Invalidate local caches only on explicit sign-out (never during refresh/auth restore).
      const uid = this.currentUserSubject.value?.uid;
      if (uid) {
        try {
          localStorage.removeItem(`quine_conversations_${uid}`);
          localStorage.removeItem(`quine_conversations_${uid}__sig`);
          localStorage.removeItem('quine_last_uid');
        } catch (e) {
          console.error('Error clearing conversation cache:', e);
        }
      }
      await signOut(this.auth);
      redirectToMarketing('/');
    } catch (error: any) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  private reauthInProgress = false;
  /**
   * Server rejected the token as invalid/revoked (e.g. the account was deleted or the session
   * revoked on another device). Drop local auth state and force a fresh sign-in.
   */
  async handleInvalidSession(): Promise<void> {
    if (this.reauthInProgress) return;
    this.reauthInProgress = true;
    try {
      try { await signOut(this.auth); } catch { /* may already be signed out */ }
      this.currentUserSubject.next(null);
      this.userConfigSubject.next(null);
      this.router.navigate(['/login']);
    } finally {
      // allow a later genuine re-auth attempt
      setTimeout(() => (this.reauthInProgress = false), 1000);
    }
  }

  /**
   * Get the current user
   */
  getCurrentUser(): User | null {
    return this.currentUserSubject.value;
  }

  /**
   * Get current user as Observable
   */
  getCurrentUserObservable(): Observable<User | null> {
    return this.currentUser$;
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.currentUserSubject.value !== null;
  }

  /**
   * Get auth instance
   */
  getAuth(): Auth {
    return this.auth;
  }

  /**
   * Handle authentication errors
   */
  private handleAuthError(error: any): Error {
    let errorMessage = 'An error occurred during authentication';
    
    switch (error.code) {
      case 'auth/popup-closed-by-user':
        errorMessage = 'Sign-in popup was closed. Please try again.';
        break;
      case 'auth/cancelled-popup-request':
        errorMessage = 'Only one popup request is allowed at a time.';
        break;
      case 'auth/popup-blocked':
        errorMessage = 'Popup was blocked by the browser. Please allow popups for this site.';
        break;
      case 'auth/unauthorized-domain':
        errorMessage = 'This domain is not authorized for OAuth operations.';
        break;
      case 'auth/operation-not-allowed':
        errorMessage = 'This sign-in method is not enabled.';
        break;
      case 'auth/email-already-in-use':
        errorMessage = 'An account with this email already exists. Please sign in instead.';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/user-disabled':
        errorMessage = 'This account has been disabled. Please contact support.';
        break;
      case 'auth/user-not-found':
        errorMessage = 'No account found with this email. Please sign up first.';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password. Please try again.';
        break;
      case 'auth/invalid-credential':
        errorMessage = 'Invalid email or password. Please try again.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later.';
        break;
      default:
        errorMessage = error.message || errorMessage;
    }
    
    return new Error(errorMessage);
  }
}
